import type {
  FormattedNewWager,
  NewWagerModalDetails,
  NewWagerSearchFilters
} from '@library/types/newWagers';
import type { ComboboxOption } from '@library/components/selects';

export const PLAYER_PLACEHOLDER_IMAGE_URL =
  'https://static.prizepicks.com/images/players/placeholder.png';

export const DEFAULT_NEW_WAGER: FormattedNewWager = {
  lineup: [
    {
      id: '',
      isPromo: false,
      leagueName: '',
      player: {
        imageUrl: '',
        name: '',
        position: '',
        team: '',
        teamName: '',
        status: 'pre_game'
      },
      predictionStats: {
        lineScore: '',
        oddsType: 'standard'
      },
      projectionStats: {
        discountName: null,
        flashSaleLineScore: null,
        startTime: ''
      },
      scoreStats: {
        details: null,
        isAutoSettled: false,
        isFinal: false,
        isOffTheBoard: false,
        score: undefined,
        undersWinDnp: false
      },
      statType: {
        name: ''
      },
      wagerType: undefined
    }
  ],
  newWager: {
    amountBetCents: 0,
    amountToWinCents: 0,
    amountWonCents: 0,
    contestAmountWonCents: 0,
    contestId: '',
    correlatedEntry: false,
    createdAt: '',
    description: '',
    eligibleForRefund: null,
    f2p: false,
    freeEntry: false,
    gameMode: '',
    id: '',
    lineupGameType: '',
    location: '',
    payoutData: null,
    payoutMultipliers: null,
    pickProtection: true,
    refundAllowedUntil: '',
    result: 'pending',
    selfRefundedAt: ''
  },
  promotion: {
    id: 0,
    name: '',
    type: null
  }
};
const commonClassValues = 'text-soClean-100 text-sm';
const commonClassContainer = 'flex flex-col items-start pb-6';

export const NEW_WAGER_MODAL_DETAILS_KEYS: Array<NewWagerModalDetails> = [
  {
    key: 'result',
    label: 'Result',
    containerClassName: 'flex flex-col items-start pb-6 border-r',
    valueClassName:
      'border-soFresh-140 rounded border px-2 py-1 text-center text-xs font-semibold uppercase'
  },
  {
    key: 'gameType',
    label: 'Game Type',
    containerClassName: `${commonClassContainer}`,
    valueClassName: commonClassValues
  },
  {
    key: 'flexPower',
    label: 'Flex/Power',
    containerClassName: `${commonClassContainer} border-r`,
    valueClassName: commonClassValues
  },
  {
    key: 'location',
    label: 'Location',
    containerClassName: `${commonClassContainer}`,
    valueClassName: commonClassValues
  },
  {
    key: 'promoType',
    label: 'Promo Type',
    containerClassName: `${commonClassContainer} border-r`,
    valueClassName: commonClassValues
  },
  {
    key: 'amountBet',
    label: 'Amount Bet',
    containerClassName: `${commonClassContainer}`,
    valueClassName: commonClassValues
  },
  {
    key: 'promoId',
    label: 'Promo ID',
    containerClassName: `${commonClassContainer} border-r`,
    valueClassName: commonClassValues
  },
  {
    key: 'amountWon',
    label: 'Amount Won',
    containerClassName: `${commonClassContainer}`,
    valueClassName: commonClassValues
  },
  {
    key: 'freeEntry',
    label: 'Free Entry',
    containerClassName: `${commonClassContainer} border-r`,
    valueClassName: commonClassValues
  },
  {
    key: 'f2p',
    label: 'F2P',
    containerClassName: `${commonClassContainer}`,
    valueClassName: commonClassValues
  },
  {
    key: 'correlated',
    label: 'Correlated',
    containerClassName: `${commonClassContainer} border-r`,
    valueClassName: commonClassValues
  },
  {
    key: 'selfRefundedAt',
    label: 'Refund at',
    containerClassName: `${commonClassContainer}`,
    valueClassName: `${commonClassValues} whitespace-pre-line h-5 normal-case`
  },
  {
    containerClassName:
      'flex-col col-span-2 !items-stretch text-xs w-full mb-6',
    key: 'payoutMultipliers',
    label: 'Payout Multipliers',
    valueClassName: `${commonClassValues} pt-2`
  }
];

export const NEW_WAGER_MODAL_DETAILS = NEW_WAGER_MODAL_DETAILS_KEYS.map(
  ({ containerClassName, key, label, valueClassName }) => {
    return {
      containerClassName,
      key,
      label,
      value: '',
      valueClassName
    };
  }
);

export const DEFAULT_ENTRIES_SEARCH_FILTERS: NewWagerSearchFilters = {
  id: undefined, // id_eq
  dateRange: {
    startDate: undefined, // created_at_gteq
    endDate: undefined // created_at_lteq
  },
  entryStatus: undefined, // result_eq
  leagueId: undefined, // predictions_projection_league_id_in
  // playerName: undefined, // predictions_projection_new_player_name_cont
  playerStatus: undefined // predictions_projection_status_eq
};

export const PLAYER_STATUS_OPTIONS: Array<ComboboxOption> = [
  {
    label: 'Pre Game',
    value: 'pre_game'
  },
  {
    label: 'In Progress',
    value: 'in_progress'
  },
  {
    label: 'Removed',
    value: 'removed'
  },
  {
    label: 'Suspended',
    value: 'suspended'
  },
  {
    label: 'Finished',
    value: 'finished'
  }
];

export const ENTRY_STATUS_OPTIONS: Array<ComboboxOption> = [
  {
    label: 'Canceled',
    value: 'canceled'
  },
  {
    label: 'Contest Lost',
    value: 'contest_lost'
  },
  {
    label: 'Contest Win',
    value: 'contest_win'
  },
  {
    label: 'Loss',
    value: 'lost'
  },
  {
    label: 'Partial Win',
    value: 'partial_win'
  },
  {
    label: 'Pending',
    value: 'pending'
  },
  {
    label: 'Push',
    value: 'pushed'
  },
  {
    label: 'Refund (Admin)',
    value: 'admin_refunded'
  },
  {
    label: 'Refund (Self)',
    value: 'refunded'
  },
  {
    label: 'Void',
    value: 'voided_mi'
  },
  {
    label: 'Win',
    value: 'won'
  }
];
